/**
 * Alert WebSocket Hook — real-time alert push via gateway WS proxy
 * TASK-381: Unified Alert System
 *
 * Connects to /ws/alerts/{accountId} through the gateway and dispatches
 * new_alert / alert_updated events into the alertStore.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { API_CONFIG } from '../config/api-config';
import { ALERT_PAGE_SIZE, useAlertStore } from '../stores/alertStore';

const PING_INTERVAL = 30_000;
const RECONNECT_DELAY = 1_000;
const MAX_RECONNECT_DELAY = 30_000;
const MAX_RECONNECT_ATTEMPTS = 5;

interface UseAlertWebSocketReturn {
  isConnected: boolean;
  connect: () => void;
  disconnect: () => void;
}

export function useAlertWebSocket(): UseAlertWebSocketReturn {
  const [isConnected, setIsConnected] = useState(false);
  const queryClient = useQueryClient();

  const wsRef = useRef<WebSocket | null>(null);
  const pingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const intentionalCloseRef = useRef(false);

  const stopPing = useCallback(() => {
    if (pingTimerRef.current) {
      clearInterval(pingTimerRef.current);
      pingTimerRef.current = null;
    }
  }, []);

  const stopReconnect = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const getWsUrl = useCallback((): string | null => {
    const accountId = localStorage.getItem('account_id');
    const token = localStorage.getItem('auth_token');
    if (!accountId) return null;

    const wsBase =
      API_CONFIG.WS_GATEWAY_URL ||
      API_CONFIG.GATEWAY_URL.replace('https://', 'wss://').replace('http://', 'ws://');

    return `${wsBase}/ws/alerts/${accountId}${token ? `?token=${encodeURIComponent(token)}` : ''}`;
  }, []);

  const connect = useCallback(() => {
    // Guard: already connected or no URL
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    const url = getWsUrl();
    if (!url) return;

    intentionalCloseRef.current = false;

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        reconnectAttemptsRef.current = 0;

        // Start keep-alive ping
        stopPing();
        pingTimerRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, PING_INTERVAL);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'pong') return;

          const store = useAlertStore.getState();

          if (data.type === 'new_alert' && data.alert) {
            store.addAlert(data.alert);
          } else if (data.type === 'alert_updated' && data.alert_id) {
            store.updateAlertStatus(data.alert_id, data.status);
          } else if (data.type === 'radar_updated') {
            // TASK-477: the radar was rebuilt server-side — refetch the authoritative payload
            // instead of piggybacking on new_alert (which fires before the rebuild commits).
            queryClient.invalidateQueries({ queryKey: ['radar'] });
            // Deep review #4: the tick's own alert changes never reach this socket. It runs in
            // the RQ radar worker, where AlertService's ConnectionManager is a different, empty
            // process-local registry — only `radar_updated` crosses processes (Redis relay). So
            // reminders emitted, escalated or superseded by a tick were invisible until a
            // reload. This frame IS the tick's "alerts changed" signal; refetch them too.
            // Deep review #5: with the page size omitted this refetch silently replaced the
            // 200-alert list the bell/Alerts page loaded with the API's 50 default, so the
            // TASK-653 primary count dropped under the RM on every tick. Same size everywhere.
            store.fetchAlerts({ limit: ALERT_PAGE_SIZE });
            store.fetchCounts();
          }
        } catch {
          // Ignore non-JSON messages
        }
      };

      ws.onerror = () => {
        setIsConnected(false);
      };

      ws.onclose = (event) => {
        setIsConnected(false);
        stopPing();

        if (intentionalCloseRef.current) return;
        if (event.code === 1000 || event.code === 1001) return;

        // Exponential backoff reconnect
        if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
          reconnectAttemptsRef.current++;
          const delay = Math.min(
            RECONNECT_DELAY * Math.pow(2, reconnectAttemptsRef.current - 1),
            MAX_RECONNECT_DELAY
          );
          reconnectTimerRef.current = setTimeout(() => connect(), delay);
        }
      };
    } catch {
      setIsConnected(false);
    }
  }, [getWsUrl, stopPing]);

  const disconnect = useCallback(() => {
    intentionalCloseRef.current = true;
    stopPing();
    stopReconnect();

    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.close(1000, 'Intentional disconnect');
      wsRef.current = null;
    }
    setIsConnected(false);
  }, [stopPing, stopReconnect]);

  // Auto-connect on mount, cleanup on unmount
  useEffect(() => {
    connect();
    return () => disconnect();
  }, [connect, disconnect]);

  return { isConnected, connect, disconnect };
}
